import React, { PropsWithChildren } from 'react'
import { AIClient, AIExecuteClient, LocalStorageApiTokenProvider, MediaClient } from '@zocks-communications/client-sdk-js'
import { addBasicRefreshTokenInterceptor } from '../../../api/client'

export interface Features {
  meeting: boolean
  chat: boolean
  raiseHand: boolean
  ai: boolean
  ondemandAIExecution: boolean
  screenShare: boolean
  participants: boolean
  leave: boolean
}

export interface ClientProviderConfiguration {
  mediaApiBaseUrl: string
  aiApiBaseUrl: string
  aiExecuteApiBaseUrl: string
  trackingEnabled: boolean
  trackingApiBaseUrl: string
}

export interface APIClientRegistry {
  mediaClient: MediaClient
  aiClient: AIClient
  aiExecuteClient: AIExecuteClient
  trackingEnabled: boolean
  trackingApiBaseUrl: string
}

const APIClientContext = React.createContext<APIClientRegistry | null>(null)
const API_TOKEN_KEY_NAME = 'authToken'

export const APIClientProvider: React.FC<PropsWithChildren<{ accountId: string; config: ClientProviderConfiguration }>> = ({ accountId, config, children }) => {
  const mediaClient = new MediaClient({
    accountId,
    apiBaseUrl: config.mediaApiBaseUrl,
    apiTokenProvider: [new LocalStorageApiTokenProvider({ apiTokenKeyName: API_TOKEN_KEY_NAME })],
  })
  const aiClient = new AIClient({
    accountId,
    aiApiBaseUrl: config.aiApiBaseUrl,
    apiTokenProvider: [new LocalStorageApiTokenProvider({ apiTokenKeyName: API_TOKEN_KEY_NAME })],
  })
  const aiExecuteClient = new AIExecuteClient({
    accountId,
    aiExecuteApiBaseUrl: config.aiExecuteApiBaseUrl,
    apiTokenProvider: [new LocalStorageApiTokenProvider({ apiTokenKeyName: API_TOKEN_KEY_NAME })],
  })
  addBasicRefreshTokenInterceptor(mediaClient.httpClient)
  addBasicRefreshTokenInterceptor(aiClient.httpClient)
  addBasicRefreshTokenInterceptor(aiExecuteClient.httpClient)
  const trackingEnabled = config.trackingEnabled
  const trackingApiBaseUrl = config.trackingApiBaseUrl
  return (
    <APIClientContext.Provider
      value={{
        mediaClient,
        aiClient,
        aiExecuteClient,
        trackingEnabled,
        trackingApiBaseUrl,
      }}
    >
      {children}
    </APIClientContext.Provider>
  )
}

export const useAPIClient = () => {
  const ctx = React.useContext(APIClientContext)
  if (!ctx) {
    throw new Error('Unable to initialize APIClientContext')
  }
  return ctx
}
